import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin, Clock, Users, BellRinging, X, CheckCircle, Envelope } from "@phosphor-icons/react";
import { api, formatEventDate, formatEventTime, splitEventTitle } from "../lib/api";
import Seo from "../components/Seo";

const NotifyMeModal = ({ event, onClose }) => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("First and last name required"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError("Enter a valid email"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/events/${event.id}/notify-me`, form);
      setSuccess(true);
      setMessage(res?.data?.message || "You're on the list.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-cyber-black/80 backdrop-blur-sm p-4 sm:p-8" data-testid="notify-me-modal" onClick={onClose}>
      <div className="hard-border bg-cyber-surface w-full max-w-lg p-7 sm:p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="notify-close">
          <X size={20} weight="bold" />
        </button>
        {success ? (
          <div className="text-center py-4" data-testid="notify-success">
            <CheckCircle size={48} weight="duotone" className="text-cyber-green mx-auto" />
            <div className="term-label text-cyber-green mt-5">/ you're on the list</div>
            <h3 className="font-display font-black tracking-tighter text-3xl mt-3">See you soon, {form.first_name.split(" ")[0]}.</h3>
            <p className="text-white/70 mt-3 text-sm">{message}</p>
            <button onClick={onClose} className="btn-ghost-neon mt-6" data-testid="notify-success-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <BellRinging size={32} weight="duotone" className="text-cyber-green" />
            <div className="term-label text-cyber-green mt-4">/ rsvp not open yet</div>
            <h3 className="font-display font-black tracking-tighter text-3xl mt-3 leading-tight">
              {event.title}
            </h3>
            <p className="text-white/70 mt-3 text-sm">
              RSVPs aren't open for this event yet. Leave your name and email and we'll notify you the moment they go live.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">First name</label>
                <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full mt-2 px-3 py-3 bg-cyber-black border border-cyber-line text-white focus:border-cyber-green focus:outline-none" data-testid="notify-first-name" disabled={submitting} />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">Last name</label>
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full mt-2 px-3 py-3 bg-cyber-black border border-cyber-line text-white focus:border-cyber-green focus:outline-none" data-testid="notify-last-name" disabled={submitting} />
              </div>
            </div>
            <div className="mt-3">
              <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/50">Email</label>
              <div className="relative mt-2">
                <Envelope size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="w-full pl-9 pr-3 py-3 bg-cyber-black border border-cyber-line text-white placeholder:text-white/30 focus:border-cyber-green focus:outline-none" data-testid="notify-email" disabled={submitting} />
              </div>
            </div>
            {error && <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-cyber-red mt-3" data-testid="notify-error">/ {error}</div>}
            <button type="submit" disabled={submitting} className="btn-neon w-full justify-center mt-6" data-testid="notify-submit">
              {submitting ? "Adding you..." : "Notify me when invitations open"}
              <ArrowUpRight size={14} weight="bold" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const TIER_LEVELS = [
  { key: "cohost", label: "event cohost", grid: "grid-cols-1 max-w-md mx-auto", card: "aspect-[16/7] p-8 sm:p-10", featured: true, badge: "COHOST" },
  { key: "presenting", label: "presenting partner", grid: "grid-cols-1 max-w-md mx-auto", card: "aspect-[16/7] p-8 sm:p-10", featured: true, badge: "PRESENTING" },
  { key: "signature", label: "signature partners", grid: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto", card: "aspect-[16/8] p-6 sm:p-8" },
  { key: "supporting", label: "supporting partners", grid: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 max-w-5xl mx-auto", card: "aspect-[16/9] p-5" },
  { key: "partners", label: "sponsors", grid: "grid-cols-1 sm:grid-cols-3 max-w-4xl mx-auto", card: "aspect-[16/8] p-6 sm:p-8" },
];

const SponsorCard = ({ sponsor, tier, index, sizeClass, featured, badge }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = sponsor.logo && !logoFailed;
  return (
    <a
      href={sponsor.website || "#"}
      target="_blank"
      rel="noreferrer"
      className={`relative group bg-white flex items-center justify-center transition-all duration-150 ${sizeClass} ${
        featured
          ? "border-2 border-cyber-green shadow-[0_0_28px_rgba(0,229,255,0.35)] hover:shadow-[0_0_44px_rgba(0,229,255,0.55)]"
          : "border border-white/10 hover:border-cyber-green"
      }`}
      data-testid={`sponsor-${tier}-${index}`}
    >
      {featured && badge && (
        <span className="absolute -top-3 left-4 bg-cyber-green text-cyber-black font-mono-tech font-bold text-[10px] tracking-[0.25em] px-3 py-1" data-testid={`sponsor-badge-${tier}`}>
          {badge}
        </span>
      )}
      {showLogo ? (
        <img
          src={sponsor.logo}
          alt={sponsor.name}
          className="w-full h-full object-contain transition-transform duration-150 group-hover:scale-[1.04]"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <div className={`font-display font-black text-center text-[#0b1024] ${tier === 'cohost' || tier === 'presenting' ? 'text-2xl sm:text-3xl' : tier === 'signature' ? 'text-xl' : 'text-base'}`}>
          {sponsor.name}
        </div>
      )}
    </a>
  );
};

const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`).then(({ data }) => setEvent(data)).catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (error) return <div className="max-w-3xl mx-auto p-12 text-white" data-testid="event-error">Event not found.</div>;
  if (!event) return <div className="max-w-3xl mx-auto p-12 text-white/40 font-mono-tech" data-testid="event-loading">/ loading...</div>;

  const rsvpActive = event.rsvp_active !== false;

  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": event.tagline || event.description,
    "startDate": event.date_tbd ? undefined : event.date,
    "endDate": event.date_tbd ? undefined : event.end_date,
    "eventStatus": event.date_tbd ? "https://schema.org/EventScheduled" : "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "image": event.hero_image ? [event.hero_image] : undefined,
    "url": `https://fintechomni.com/events/${event.slug}`,
    "location": {
      "@type": "Place",
      "name": event.venue,
      "address": [event.location, event.city].filter(Boolean).join(", "),
    },
    "organizer": {
      "@type": "Organization",
      "name": "FintechOmni",
      "url": "https://fintechomni.com",
    },
    "offers": rsvpActive ? {
      "@type": "Offer",
      "url": `https://fintechomni.com/events/${event.slug}/rsvp`,
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
    } : undefined,
  };

  return (
    <main className="grain" data-testid={`event-detail-${event.slug}`}>
      <Seo
        path={`/events/${event.slug}`}
        title={event.title}
        description={event.tagline || (event.description || "").slice(0, 200)}
        image={event.hero_image}
        type="event"
        jsonLd={eventJsonLd}
      />
      {/* HERO */}
      <section className="relative border-b border-cyber-line">
        {event.hero_image && (
          <div className="absolute inset-0">
            <img src={event.hero_image} alt={event.title} className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-cyber-black/60 via-cyber-black/80 to-cyber-black" />
          </div>
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <Link to="/events" className="font-mono-tech text-xs text-cyber-yellow tracking-[0.2em] uppercase">← All events</Link>
          <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="term-label mb-4">/ event_id::{event.id.slice(0, 8)}</div>
              <h1 className="font-display font-black tracking-tighter text-[44px] sm:text-7xl lg:text-8xl leading-[0.9]">
                {splitEventTitle(event.title).map((line, i) => (
                  <span key={`${line}-${i}`} className="block">{line}</span>
                ))}
              </h1>
              <p className="mt-5 sm:mt-6 text-white/80 text-base sm:text-xl max-w-2xl">{event.tagline}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <div className="hard-border p-5 bg-cyber-surface/80 backdrop-blur-sm">
                <div className="term-label flex items-center gap-2"><Calendar size={12} /> date</div>
                {event.date_tbd ? (
                  <>
                    <div className="font-mono-tech text-cyber-yellow mt-2">TBD</div>
                    <div className="font-mono-tech text-white/60 text-sm">Date and time to be announced</div>
                  </>
                ) : (
                  <>
                    <div className="font-mono-tech text-cyber-yellow mt-2">{formatEventDate(event.date)}</div>
                    <div className="font-mono-tech text-white/60 text-sm">{formatEventTime(event.date)} → {event.end_date ? formatEventTime(event.end_date) : 'late'}</div>
                  </>
                )}
              </div>
              <div className="hard-border p-5 bg-cyber-surface/80 backdrop-blur-sm">
                <div className="term-label flex items-center gap-2"><MapPin size={12} /> venue</div>
                <div className="font-mono-tech text-cyber-yellow mt-2">{event.venue}</div>
                <div className="font-mono-tech text-white/60 text-sm">{event.location && event.city && event.location.toLowerCase().includes(event.city.toLowerCase()) ? event.location : [event.city, event.location].filter(Boolean).join(", ")}</div>
              </div>
              <div className="hard-border p-5 bg-cyber-surface/80 backdrop-blur-sm">
                <div className="term-label flex items-center gap-2"><Users size={12} /> capacity</div>
                <div className="font-mono-tech text-cyber-yellow mt-2">{event.capacity} Attendees</div>
              </div>
              {rsvpActive ? (
                <Link to={`/events/${event.slug}/rsvp`} className="btn-neon justify-center" data-testid="rsvp-cta">
                  Request an Invitation <ArrowUpRight size={16} weight="bold" />
                </Link>
              ) : (
                <button type="button" onClick={() => setNotifyOpen(true)} className="btn-neon justify-center" data-testid="notify-me-cta">
                  Notify me <BellRinging size={16} weight="bold" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="term-label">/ briefing</div>
          </div>
          <div className="lg:col-span-8">
            <p className="text-white/80 text-xl leading-relaxed whitespace-pre-line">{event.description}</p>
          </div>
        </div>
      </section>

      {/* AGENDA */}
      {event.show_agenda !== false && event.agenda && event.agenda.length > 0 && (
        <section className="border-b border-cyber-line bg-cyber-black" data-testid="event-agenda">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
              <div className="lg:col-span-4">
                <div className="term-label mb-2">/ agenda</div>
                <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Programme.</h2>
              </div>
              <div className="lg:col-span-8 text-white/60">
                Dense, intentional, no filler. Times in local timezone.
              </div>
            </div>
            <div className="hard-border">
              {event.agenda.map((row, i) => (
                <div key={`${row.time}-${row.title}-${i}`} className={`grid grid-cols-12 gap-4 px-5 sm:px-8 py-5 ${i !== event.agenda.length - 1 ? 'border-b border-cyber-line' : ''}`}>
                  <div className="col-span-3 sm:col-span-2 font-mono-tech text-cyber-yellow flex items-center gap-2">
                    <Clock size={12} /> {row.time}
                  </div>
                  <div className="col-span-9 sm:col-span-7 font-display text-xl text-white">{row.title}</div>
                  <div className="col-span-12 sm:col-span-3 font-mono-tech text-xs text-white/50 uppercase tracking-[0.2em] sm:text-right self-center">{row.speaker || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPEAKERS */}
      {event.show_speakers !== false && event.speakers && event.speakers.length > 0 && (
        <section className="border-b border-cyber-line" data-testid="event-speakers">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
              <div className="lg:col-span-4">
                <div className="term-label mb-2">/ speakers</div>
                <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Operators on stage.</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {event.speakers.map((s, i) => (
                <div key={s.name || i} className="group hard-border bg-cyber-surface overflow-hidden">
                  {s.image && <div className="aspect-square overflow-hidden"><img src={s.image} alt={s.name} className="w-full h-full object-cover img-grey" /></div>}
                  <div className="p-5">
                    <div className="font-display font-black text-xl">{s.name}</div>
                    <div className="font-mono-tech text-cyber-yellow text-xs uppercase tracking-[0.2em] mt-1">{s.role}</div>
                    <p className="text-white/60 text-sm mt-3">{s.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {event.show_sponsors !== false && event.sponsors && event.sponsors.length > 0 && (
        <section className="border-b border-cyber-line bg-cyber-black" data-testid="event-sponsors">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
              <div className="lg:col-span-4">
                <div className="term-label mb-2">/ sponsors</div>
                <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">{event.sponsors_title || "Powered by."}</h2>
              </div>
              <div className="lg:col-span-8 text-white/60">
                {event.sponsors_intro || "Don't miss what these category leaders are doing in fintech marketing — they're the operators sponsoring this event."}
              </div>
            </div>
            {/* Group by sponsorship level */}
            {TIER_LEVELS.map(({ key, label, grid, card, featured, badge }) => {
              const tierSponsors = event.sponsors.filter((s) => s.tier === key);
              if (tierSponsors.length === 0) return null;
              return (
                <div key={key} className="mb-12 last:mb-0" data-testid={`sponsor-tier-${key}`}>
                  <div className="term-label mb-4 text-center">/ {label}</div>
                  <div className={`grid gap-3 ${grid}`}>
                    {tierSponsors.map((s, i) => (
                      <SponsorCard key={`${key}-${s.name || i}`} sponsor={s} tier={key} index={i} sizeClass={card} featured={featured} badge={badge} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-cyber-yellow text-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-[0.2em]">/ {rsvpActive ? "ready to attend?" : "not open yet"}</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl mt-2 leading-none">
              {rsvpActive ? "Request your invitation." : "Get on the list."}
            </h2>
          </div>
          {rsvpActive ? (
            <Link to={`/events/${event.slug}/rsvp`} className="bg-cyber-black text-cyber-yellow border border-cyber-black hover:bg-cyber-black/80 px-7 py-4 font-mono-tech tracking-[0.12em] uppercase font-bold text-sm transition-colors duration-150" data-testid="cta-rsvp-bottom">
              Request an Invitation →
            </Link>
          ) : (
            <button type="button" onClick={() => setNotifyOpen(true)} className="bg-cyber-black text-cyber-yellow border border-cyber-black hover:bg-cyber-black/80 px-7 py-4 font-mono-tech tracking-[0.12em] uppercase font-bold text-sm transition-colors duration-150" data-testid="cta-notify-bottom">
              Notify me →
            </button>
          )}
        </div>
      </section>

      {notifyOpen && <NotifyMeModal event={event} onClose={() => setNotifyOpen(false)} />}
    </main>
  );
};

export default EventDetail;
